import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

export type SyncEventType =
  | 'check_in'
  | 'check_out'
  | 'rest_start'
  | 'rest_end'
  | 'change_task'

export interface QueuedEvent {
  client_event_id: string
  event_type: SyncEventType
  occurred_at_local: string
  latitude: number
  longitude: number
  accuracy: number
  task_id?: number
  project_id?: number
  comment?: string
  attempts: number
  last_error?: string
}

export interface AuthTokens {
  access_token: string
  refresh_token: string
  device_id: string
  employee_id: number
  employee_name: string
  tenant_code: string
  // Código de idioma de Odoo del empleado (p.ej. 'es_ES', 'ar_001').
  // Lo devuelve la API en el onboard; decide el idioma de la PWA.
  lang?: string
}

interface FichajeDB extends DBSchema {
  queue: {
    key: string
    value: QueuedEvent
    indexes: { 'by-occurred-at': string }
  }
  auth: {
    key: 'tokens'
    value: AuthTokens
  }
  cache: {
    key: string
    value: { key: string; value: unknown; updated_at: number }
  }
}

let dbPromise: Promise<IDBPDatabase<FichajeDB>> | null = null

export function getDb(): Promise<IDBPDatabase<FichajeDB>> {
  if (!dbPromise) {
    dbPromise = openDB<FichajeDB>('fichaje', 1, {
      upgrade(db) {
        const queue = db.createObjectStore('queue', { keyPath: 'client_event_id' })
        queue.createIndex('by-occurred-at', 'occurred_at_local')
        db.createObjectStore('auth')
        db.createObjectStore('cache', { keyPath: 'key' })
      },
    })
  }
  return dbPromise
}

export async function enqueue(event: QueuedEvent): Promise<void> {
  const db = await getDb()
  await db.put('queue', event)
}

export async function listQueue(): Promise<QueuedEvent[]> {
  const db = await getDb()
  return db.getAllFromIndex('queue', 'by-occurred-at')
}

export async function removeFromQueue(clientEventId: string): Promise<void> {
  const db = await getDb()
  await db.delete('queue', clientEventId)
}

export async function markFailure(clientEventId: string, reason: string): Promise<void> {
  const db = await getDb()
  const ev = await db.get('queue', clientEventId)
  if (ev) {
    ev.attempts = (ev.attempts || 0) + 1
    ev.last_error = reason
    await db.put('queue', ev)
  }
}

export async function getTokens(): Promise<AuthTokens | undefined> {
  const db = await getDb()
  return db.get('auth', 'tokens')
}

export async function setTokens(t: AuthTokens): Promise<void> {
  const db = await getDb()
  await db.put('auth', t, 'tokens')
}

export async function clearTokens(): Promise<void> {
  const db = await getDb()
  await db.delete('auth', 'tokens')
}

export async function cacheGet<T>(key: string): Promise<T | undefined> {
  const db = await getDb()
  const rec = await db.get('cache', key)
  return rec ? (rec.value as T) : undefined
}

export async function cacheSet<T>(key: string, value: T): Promise<void> {
  const db = await getDb()
  await db.put('cache', { key, value, updated_at: Date.now() })
}
